/**
 * flash.events.* event classes. Internal dispatch state lives in `$`-prefixed fields so that
 * game code can freely attach dynamic properties (events are dynamic in the contract).
 */

export const EventPhase = { CAPTURING_PHASE: 1, AT_TARGET: 2, BUBBLING_PHASE: 3 } as const;

export class Event {
  static readonly ENTER_FRAME = 'enterFrame' as const;
  static readonly EXIT_FRAME = 'exitFrame' as const;
  static readonly FRAME_CONSTRUCTED = 'frameConstructed' as const;
  static readonly COMPLETE = 'complete' as const;
  static readonly CHANGE = 'change' as const;
  static readonly ADDED = 'added' as const;
  static readonly ADDED_TO_STAGE = 'addedToStage' as const;
  static readonly REMOVED = 'removed' as const;
  static readonly REMOVED_FROM_STAGE = 'removedFromStage' as const;
  static readonly RESIZE = 'resize' as const;
  static readonly ACTIVATE = 'activate' as const;
  static readonly DEACTIVATE = 'deactivate' as const;
  static readonly INIT = 'init' as const;
  static readonly OPEN = 'open' as const;
  static readonly CLOSE = 'close' as const;
  static readonly SOUND_COMPLETE = 'soundComplete' as const;
  static readonly RENDER = 'render' as const;
  static readonly SELECT = 'select' as const;
  static readonly SCROLL = 'scroll' as const;
  static readonly TAB_CHILDREN_CHANGE = 'tabChildrenChange' as const;
  static readonly TAB_ENABLED_CHANGE = 'tabEnabledChange' as const;
  static readonly TAB_INDEX_CHANGE = 'tabIndexChange' as const;
  static readonly UNLOAD = 'unload' as const;
  static readonly MOUSE_LEAVE = 'mouseLeave' as const;
  static readonly FULLSCREEN = 'fullScreen' as const;
  static readonly CANCEL = 'cancel' as const;
  static readonly CONNECT = 'connect' as const;
  static readonly ID3 = 'id3' as const;

  [key: string]: any;

  /** @internal */ $type: string;
  /** @internal */ $bubbles: boolean;
  /** @internal */ $cancelable: boolean;
  /** @internal */ $target: any;
  /** @internal */ $currentTarget: any;
  /** @internal */ $phase: number;
  /** @internal */ $stop: boolean;
  /** @internal */ $stopImmediate: boolean;
  /** @internal */ $prevented: boolean;

  constructor(type: string, bubbles: boolean = false, cancelable: boolean = false) {
    this.$type = String(type);
    this.$bubbles = !!bubbles;
    this.$cancelable = !!cancelable;
    this.$target = null;
    this.$currentTarget = null;
    this.$phase = EventPhase.AT_TARGET;
    this.$stop = false;
    this.$stopImmediate = false;
    this.$prevented = false;
  }

  get type(): string {
    return this.$type;
  }
  get bubbles(): boolean {
    return this.$bubbles;
  }
  get cancelable(): boolean {
    return this.$cancelable;
  }
  get target(): any {
    return this.$target;
  }
  get currentTarget(): any {
    return this.$currentTarget;
  }
  get eventPhase(): number {
    return this.$phase;
  }

  clone(): Event {
    return new Event(this.$type, this.$bubbles, this.$cancelable);
  }
  stopPropagation(): void {
    this.$stop = true;
  }
  stopImmediatePropagation(): void {
    this.$stop = true;
    this.$stopImmediate = true;
  }
  preventDefault(): void {
    if (this.$cancelable) this.$prevented = true;
  }
  isDefaultPrevented(): boolean {
    return this.$prevented;
  }
  formatToString(className: string, ...args: string[]): string {
    return `[${className}${args.map((a) => ` ${a}=${JSON.stringify((this as any)[a])}`).join('')}]`;
  }
  toString(): string {
    return this.formatToString('Event', 'type', 'bubbles', 'cancelable', 'eventPhase');
  }
}

export class MouseEvent extends Event {
  static readonly CLICK = 'click' as const;
  static readonly DOUBLE_CLICK = 'doubleClick' as const;
  static readonly MOUSE_DOWN = 'mouseDown' as const;
  static readonly MOUSE_UP = 'mouseUp' as const;
  static readonly MOUSE_MOVE = 'mouseMove' as const;
  static readonly MOUSE_OVER = 'mouseOver' as const;
  static readonly MOUSE_OUT = 'mouseOut' as const;
  static readonly ROLL_OVER = 'rollOver' as const;
  static readonly ROLL_OUT = 'rollOut' as const;
  static readonly MOUSE_WHEEL = 'mouseWheel' as const;
  static readonly RELEASE_OUTSIDE = 'releaseOutside' as const;
  static readonly RIGHT_CLICK = 'rightClick' as const;
  static readonly RIGHT_MOUSE_DOWN = 'rightMouseDown' as const;
  static readonly RIGHT_MOUSE_UP = 'rightMouseUp' as const;
  static readonly MIDDLE_CLICK = 'middleClick' as const;
  static readonly MIDDLE_MOUSE_DOWN = 'middleMouseDown' as const;
  static readonly MIDDLE_MOUSE_UP = 'middleMouseUp' as const;
  static readonly CONTEXT_MENU = 'contextMenu' as const;

  localX: number;
  localY: number;
  delta: number;
  buttonDown: boolean;
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  relatedObject: any;
  /** @internal stage coordinates (pixels) set by the player when dispatching input. */
  $stageX: number;
  /** @internal */ $stageY: number;

  constructor(
    type: string,
    bubbles: boolean = true,
    cancelable: boolean = false,
    localX: number = NaN,
    localY: number = NaN,
    relatedObject: any = null,
    ctrlKey: boolean = false,
    altKey: boolean = false,
    shiftKey: boolean = false,
    buttonDown: boolean = false,
    delta: number = 0,
  ) {
    super(type, bubbles, cancelable);
    this.localX = localX;
    this.localY = localY;
    this.relatedObject = relatedObject;
    this.ctrlKey = ctrlKey;
    this.altKey = altKey;
    this.shiftKey = shiftKey;
    this.buttonDown = buttonDown;
    this.delta = delta;
    this.$stageX = NaN;
    this.$stageY = NaN;
  }

  get stageX(): number {
    return this.$stageX;
  }
  get stageY(): number {
    return this.$stageY;
  }
  updateAfterEvent(): void {
    /* rendering is driven by the host; nothing to do */
  }
  clone(): MouseEvent {
    const e = new MouseEvent(
      this.$type,
      this.$bubbles,
      this.$cancelable,
      this.localX,
      this.localY,
      this.relatedObject,
      this.ctrlKey,
      this.altKey,
      this.shiftKey,
      this.buttonDown,
      this.delta,
    );
    e.$stageX = this.$stageX;
    e.$stageY = this.$stageY;
    return e;
  }
  toString(): string {
    return this.formatToString('MouseEvent', 'type', 'bubbles', 'cancelable', 'eventPhase', 'localX', 'localY', 'stageX', 'stageY', 'relatedObject', 'ctrlKey', 'altKey', 'shiftKey', 'buttonDown', 'delta');
  }
}

export class KeyboardEvent extends Event {
  static readonly KEY_DOWN = 'keyDown' as const;
  static readonly KEY_UP = 'keyUp' as const;

  keyCode: number;
  charCode: number;
  keyLocation: number;
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;

  constructor(
    type: string,
    bubbles: boolean = true,
    cancelable: boolean = false,
    charCode: number = 0,
    keyCode: number = 0,
    keyLocation: number = 0,
    ctrlKey: boolean = false,
    altKey: boolean = false,
    shiftKey: boolean = false,
  ) {
    super(type, bubbles, cancelable);
    this.charCode = charCode >>> 0;
    this.keyCode = keyCode >>> 0;
    this.keyLocation = keyLocation >>> 0;
    this.ctrlKey = ctrlKey;
    this.altKey = altKey;
    this.shiftKey = shiftKey;
  }
  updateAfterEvent(): void {
    /* no-op */
  }
  clone(): KeyboardEvent {
    return new KeyboardEvent(this.$type, this.$bubbles, this.$cancelable, this.charCode, this.keyCode, this.keyLocation, this.ctrlKey, this.altKey, this.shiftKey);
  }
  toString(): string {
    return this.formatToString('KeyboardEvent', 'type', 'bubbles', 'cancelable', 'eventPhase', 'charCode', 'keyCode', 'keyLocation', 'ctrlKey', 'altKey', 'shiftKey');
  }
}

export class TimerEvent extends Event {
  static readonly TIMER = 'timer' as const;
  static readonly TIMER_COMPLETE = 'timerComplete' as const;
  constructor(type: string, bubbles: boolean = false, cancelable: boolean = false) {
    super(type, bubbles, cancelable);
  }
  updateAfterEvent(): void {
    /* no-op */
  }
  clone(): TimerEvent {
    return new TimerEvent(this.$type, this.$bubbles, this.$cancelable);
  }
}

export class TextEvent extends Event {
  static readonly TEXT_INPUT = 'textInput' as const;
  static readonly LINK = 'link' as const;
  text: string;
  constructor(type: string, bubbles: boolean = false, cancelable: boolean = false, text: string = '') {
    super(type, bubbles, cancelable);
    this.text = text;
  }
  clone(): TextEvent {
    return new TextEvent(this.$type, this.$bubbles, this.$cancelable, this.text);
  }
}

export class FocusEvent extends Event {
  static readonly FOCUS_IN = 'focusIn' as const;
  static readonly FOCUS_OUT = 'focusOut' as const;
  static readonly KEY_FOCUS_CHANGE = 'keyFocusChange' as const;
  static readonly MOUSE_FOCUS_CHANGE = 'mouseFocusChange' as const;
  relatedObject: any;
  shiftKey: boolean;
  keyCode: number;
  constructor(type: string, bubbles: boolean = true, cancelable: boolean = false, relatedObject: any = null, shiftKey: boolean = false, keyCode: number = 0) {
    super(type, bubbles, cancelable);
    this.relatedObject = relatedObject;
    this.shiftKey = shiftKey;
    this.keyCode = keyCode;
  }
  clone(): FocusEvent {
    return new FocusEvent(this.$type, this.$bubbles, this.$cancelable, this.relatedObject, this.shiftKey, this.keyCode);
  }
}

export class ErrorEvent extends TextEvent {
  static readonly ERROR = 'error' as const;
  errorID: number;
  constructor(type: string, bubbles: boolean = false, cancelable: boolean = false, text: string = '', id: number = 0) {
    super(type, bubbles, cancelable, text);
    this.errorID = id;
  }
  clone(): ErrorEvent {
    return new ErrorEvent(this.$type, this.$bubbles, this.$cancelable, this.text, this.errorID);
  }
}

export class IOErrorEvent extends ErrorEvent {
  static readonly IO_ERROR = 'ioError' as const;
  constructor(type: string, bubbles: boolean = false, cancelable: boolean = false, text: string = '', id: number = 0) {
    super(type, bubbles, cancelable, text, id);
  }
  clone(): IOErrorEvent {
    return new IOErrorEvent(this.$type, this.$bubbles, this.$cancelable, this.text, this.errorID);
  }
}

export class SecurityErrorEvent extends ErrorEvent {
  static readonly SECURITY_ERROR = 'securityError' as const;
  clone(): SecurityErrorEvent {
    return new SecurityErrorEvent(this.$type, this.$bubbles, this.$cancelable, this.text, this.errorID);
  }
}

export class ProgressEvent extends Event {
  static readonly PROGRESS = 'progress' as const;
  static readonly SOCKET_DATA = 'socketData' as const;
  bytesLoaded: number;
  bytesTotal: number;
  constructor(type: string, bubbles: boolean = false, cancelable: boolean = false, bytesLoaded: number = 0, bytesTotal: number = 0) {
    super(type, bubbles, cancelable);
    this.bytesLoaded = bytesLoaded;
    this.bytesTotal = bytesTotal;
  }
  clone(): ProgressEvent {
    return new ProgressEvent(this.$type, this.$bubbles, this.$cancelable, this.bytesLoaded, this.bytesTotal);
  }
}
